<template>
  <div>
    <h2 v-if="props.user && props.user.prenom && props.user.nom" class="font-semibold my-0 text-center">
      {{ props.user.prenom }} {{ props.user.nom }}
    </h2>
    <h2 class="font-normal text-center mt-3 mb-7">Identifiez-vous</h2>

    <form class="form" @submit.prevent="onSubmit">
      <!-- Upload Photo de profil -->
      <label 
        for="file-upload-photo" 
        class="custom-upload"
        :class="{ active: formData.photo }"
      >
        <div class="custom-upload-st2">
          <input 
            id="file-upload-photo" 
            name="file-upload-photo" 
            type="file"
            accept="image/*"
            @change="handlePhotoUpload"
          >
          <div 
            class="s5 custom-upload-img" 
            :style="photoPreviewStyle"
          >
            <img 
              v-if="!photoPreview" 
              src="/public/icons/icon-photo.svg" 
              width="45%" 
              class="icon-white" 
            />
          </div>
          <div class="s10">
            <span class="custom-upload-text">Photo de profil</span><br>
            <span class="custom-upload-text-sm">
              {{ formData.photo ? formData.photo.name : 'Charger la photo' }}
            </span>
          </div>
          <div class="s5 right">
            <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
          </div>
        </div>
      </label>
      <span v-if="errors.photo" class="text-red">{{ errors.photo }}</span>

      <div class="h-10"></div>

      <!-- Type et numéro de pièce d'identité -->
      <div class="form-field-split active rounded-20 mt-5 my-6">
        <div class="form-field-floating full lg w-40">
          <select 
            class="form-input-select" 
            name="type_piece_identite" 
            id="type_piece_identite"
            v-model="formData.type_piece_identite"
          >
            <option value="" selected>Type de pièce</option>
            <option :value="key" v-for="(value, key) in props.types_piece_identites" :key="key">
              {{ value }}
            </option>
          </select>
          <label for="type_piece_identite">Pièce d'identité <b class="color-danger">*</b></label>
        </div>
        <hr class="hr" />
        <div class="form-field-floating lg reactive w-60">
          <input 
            type="text" 
            class="form-input" 
            name="numero_piece_identite" 
            id="numero_piece_identite" 
            v-model="formData.numero_piece_identite"
            placeholder=""
          >
          <label for="numero_piece_identite">N° de pièce d'identité <b class="color-danger">*</b></label>
        </div>
      </div>
      <span v-if="errors.type_piece_identite" class="text-red">{{ errors.type_piece_identite }}</span>
      <span v-if="errors.numero_piece_identite" class="text-red">{{ errors.numero_piece_identite }}</span>

      <!-- Upload Pièce Recto -->
      <label 
        for="file-upload-recto" 
        class="custom-upload my-5"
        :class="{ active: formData.piece_recto }"
      >
        <div class="custom-upload-st2">
          <input 
            id="file-upload-recto" 
            name="file-upload-recto" 
            type="file"
            accept="image/*,.pdf"
            @change="handleRectoUpload"
          >
          <div 
            class="s5 custom-upload-img" 
            :style="rectoPreviewStyle"
          >
            <img 
              v-if="!rectoPreview" 
              src="/public/icons/icon-idcard-recto.svg" 
              width="45%" 
              class="icon-white" 
            />
          </div>
          <div class="s10">
            <span class="custom-upload-text uppercase">Pièce recto</span> 
            <b class="color-danger">*</b><br>
            <span class="custom-upload-text-sm">
              {{ formData.piece_recto ? formData.piece_recto.name : 'Charger l\'image' }}
            </span>
          </div>
          <div class="s5 right">
            <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
          </div>
        </div>
      </label>
      <span v-if="errors.piece_recto" class="text-red">{{ errors.piece_recto }}</span>

      <!-- Upload Pièce Verso -->
      <label 
        for="file-upload-verso" 
        class="custom-upload"
        :class="{ active: formData.piece_verso }"
      >
        <div class="custom-upload-st2">
          <input 
            id="file-upload-verso" 
            name="file-upload-verso" 
            type="file"
            accept="image/*,.pdf"
            @change="handleVersoUpload"
          >
          <div 
            class="s5 custom-upload-img" 
            :style="versoPreviewStyle"
          >
            <img 
              v-if="!versoPreview" 
              src="/public/icons/icon-idcard-verso.svg" 
              width="45%" 
              class="icon-white" 
            />
          </div>
          <div class="s10">
            <span class="custom-upload-text uppercase">Pièce verso</span> 
            <b class="color-danger">*</b><br>
            <span class="custom-upload-text-sm">
              {{ formData.piece_verso ? formData.piece_verso.name : 'Charger l\'image' }}
            </span>
          </div>
          <div class="s5 right">
            <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
          </div>
        </div>
      </label>
      <span v-if="errors.piece_verso" class="text-red">{{ errors.piece_verso }}</span>
      
      <p class="text-read text-grey-600 text-center mt-6">
        Format de fichier supporté: PDF, JPG, PNG, GIF. Taille max: 5MB.
      </p>

      <span v-if="errorMessage" class="text-red">{{ errorMessage }}</span>

      <button type="submit" class="btn lg w-100 flex-center mt-5">
        <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
        <span>Valider</span>
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, reactive, ref, computed } from 'vue'
import { showAlert } from '@/utils/swal'

interface UserSummary {
  nom: string
  prenom: string
  email: string
  telephone: string
}

const props = defineProps<{
  user: UserSummary
  types_piece_identites: Record<string, string>
}>()

const emit = defineEmits<{
  (e: 'saved', payload: typeof formData): void
}>()

const errorMessage = ref('')
const formData = reactive({
  photo: null as { file: File; name: string; preview: string | ArrayBuffer | null } | null,
  type_piece_identite: '',
  numero_piece_identite: '',
  piece_recto: null as { file: File; name: string; preview: string | ArrayBuffer | null } | null,
  piece_verso: null as { file: File; name: string; preview: string | ArrayBuffer | null } | null
})

const errors = reactive<Record<string, string>>({
  photo: '',
  piece_recto: '',
  piece_verso: '',
  type_piece_identite: '',
  numero_piece_identite: ''
})

const photoPreview = ref<string>('')
const rectoPreview = ref<string>('')
const versoPreview = ref<string>('')

const photoPreviewStyle = computed(() => {
  if (photoPreview.value) {
    return `background: url('${photoPreview.value}'); background-size: cover;`
  }
  return "background: url('/public/images/empty.png'); background-size: cover;"
})

const rectoPreviewStyle = computed(() => {
  if (rectoPreview.value) {
    return `background: url('${rectoPreview.value}'); background-size: cover;`
  }
  return "background: url('/public/images/empty.png'); background-size: cover;"
})

const versoPreviewStyle = computed(() => {
  if (versoPreview.value) {
    return `background: url('${versoPreview.value}'); background-size: cover;`
  }
  return "background: url('/public/images/empty.png'); background-size: cover;"
})

const validateFile = (file: File): boolean => {
  const maxSize = 5 * 1024 * 1024 // 5MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf']

  if (!allowedTypes.includes(file.type)) {
    showAlert.error('Format de fichier non supporté. Veuillez utiliser PDF, JPG, PNG ou GIF.')
    return false
  }

  if (file.size > maxSize) {
    showAlert.error('La taille du fichier dépasse 5MB.')
    return false
  }

  return true
}

const createPreview = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (e) => {
        resolve(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    } else {
      resolve('')
    }
  })
}

const handlePhotoUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]

  if (file && validateFile(file)) {
    const preview = await createPreview(file)
    formData.photo = {
      file: file,
      name: file.name,
      preview: preview
    }
    photoPreview.value = preview
    errors.photo = ''
  } else {
    target.value = ''
    formData.photo = null
    photoPreview.value = ''
  }
}

const handleRectoUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]

  if (file && validateFile(file)) {
    const preview = await createPreview(file)
    formData.piece_recto = {
      file: file,
      name: file.name,
      preview: preview
    }
    rectoPreview.value = preview
    errors.piece_recto = ''
  } else {
    target.value = ''
    formData.piece_recto = null
    rectoPreview.value = ''
  }
}

const handleVersoUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]

  if (file && validateFile(file)) {
    const preview = await createPreview(file)
    formData.piece_verso = {
      file: file,
      name: file.name,
      preview: preview
    }
    versoPreview.value = preview
    errors.piece_verso = ''
  } else {
    target.value = ''
    formData.piece_verso = null
    versoPreview.value = ''
  }
}

function validate() {
  let isValid = true
  Object.keys(errors).forEach(key => {
    errors[key] = ''
  })

  if (!formData.photo) {
    errors.photo = 'La photo est requise'
    isValid = false
  }
  if (!formData.piece_recto) {
    errors.piece_recto = 'La pièce recto est requise'
    isValid = false
  }
  if (!formData.piece_verso) {
    errors.piece_verso = 'La pièce verso est requise'
    isValid = false
  }
  if (!formData.type_piece_identite) {
    errors.type_piece_identite = "Le type de pièce d'identité est requis"
    isValid = false
  }
  if (!formData.numero_piece_identite) {
    errors.numero_piece_identite = "Le numéro de pièce d'identité est requis"
    isValid = false
  }

  return isValid
}

function onSubmit() {
  if (!validate()) {
    return
  }
  errorMessage.value = ''
  emit('saved', { ...formData })
}
</script>

<style scoped>
/* Styles spécifiques pour masquer les inputs file */
input[type="file"] {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.custom-upload {
  cursor: pointer;
  transition: all 0.3s ease;
}

.custom-upload:hover {
  opacity: 0.9;
}

.custom-upload-img {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
}
</style>
